
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { Canvas, Meta, Story } from '@storybook/addon-docs';
import inlineSizeDemo from './demo/inline-size.twig';
import responsiveDemo from './demo/responsive.twig';
import './demo/styles.scss';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Utilities/Size" mdxType="Meta" />
    <h1>{`Size`}</h1>
    <p>{`Utilities for specifying the size occupied by an element. While largely unnecessary in objects and components thanks to `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout"
      }}>{`CSS Grid Layout`}</a>{`, these utilities can still be useful for fine-tuning one-off elements in page content.`}</p>
    <h2>{`Logical Properties`}</h2>
    <p>{`All of our utilities use `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/css-logical-1/"
      }}>{`logical properties`}</a>{` instead of specific directions `}<a parentName="p" {...{
        "href": "https://caniuse.com/#feat=css-logical-props"
      }}>{`in supported browsers`}</a>{` to simplify potential localization projects.`}</p>
    <p>{`The following table maps the traditional physical property for `}<inlineCode parentName="p">{`direction: ltr`}</inlineCode>{` and `}<inlineCode parentName="p">{`writing-mode: horizontal-tb`}</inlineCode>{` to its equivalent logical property:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Physical Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Logical Property`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`height`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`block-size`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`width`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`inline-size`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`You can observe how utilities respond to various `}<inlineCode parentName="p">{`direction`}</inlineCode>{` and `}<inlineCode parentName="p">{`writing-mode`}</inlineCode>{` values by selecting a different "Text flow" in this project's toolbar.`}</p>
    <h2>{`Inline size`}</h2>
    <p>{`Utilities beginning with `}<inlineCode parentName="p">{`u-size-inline-`}</inlineCode>{` specify the `}<inlineCode parentName="p">{`inline-size`}</inlineCode>{` of an element:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`u-size-inline-{fraction}`}</inlineCode>{` for percentage widths, where `}<inlineCode parentName="li">{`{fraction}`}</inlineCode>{` represents the desired ratio. For example, `}<inlineCode parentName="li">{`u-size-inline-2/5`}</inlineCode>{` would set `}<inlineCode parentName="li">{`inline-size`}</inlineCode>{` to `}<inlineCode parentName="li">{`40%`}</inlineCode>{`. Fractions with denominators of `}<inlineCode parentName="li">{`2`}</inlineCode>{` through `}<inlineCode parentName="li">{`6`}</inlineCode>{` are supported.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-size-inline-full`}</inlineCode>{` sets the `}<inlineCode parentName="li">{`inline-size`}</inlineCode>{` to `}<inlineCode parentName="li">{`100%`}</inlineCode>{` of the container.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`u-size-inline-auto`}</inlineCode>{` sets the `}<inlineCode parentName="li">{`inline-size`}</inlineCode>{` to the natural value for the element.`}</li>
    </ul>
    <Canvas mdxType="Canvas">
  <Story name="Inline size (Width)" mdxType="Story">{() => inlineSizeDemo()}</Story>
    </Canvas>
    <h2>{`Responsive`}</h2>
    <p>{`All of these utilities include responsive variations. Appending `}<inlineCode parentName="p">{`@{breakpoint}`}</inlineCode>{` to the end of the class name will cause the utility to change for the given breakpoint.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Responsive" mdxType="Story">{() => responsiveDemo()}</Story>
    </Canvas>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const inlineSizeWidth = (() => inlineSizeDemo());
inlineSizeWidth.storyName = 'Inline size (Width)';
inlineSizeWidth.parameters = { storySource: { source: '() => inlineSizeDemo()' } };

export const responsive = (() => responsiveDemo());
responsive.storyName = 'Responsive';
responsive.parameters = { storySource: { source: '() => responsiveDemo()' } };

const componentMeta = { title: 'Utilities/Size', includeStories: ["inlineSizeWidth","responsive"],  };

const mdxStoryNameToKey = {"Inline size (Width)":"inlineSizeWidth","Responsive":"responsive"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;